import { type ApiClient, createEntityStore, type EntityWithId, runStoreAction } from "./shared";

export interface CrudEntity extends EntityWithId {
	readonly [key: string]: unknown;
}

export interface CrudStoreBundle<TEntity extends CrudEntity, TAddInput, TUpdateInput extends { readonly id: string }> {
	readonly actions: {
		fetchAll: () => Promise<TEntity[]>;
		fetchById: (id: string) => Promise<TEntity>;
		add: (input: TAddInput) => Promise<TEntity>;
		update: (input: TUpdateInput) => Promise<TEntity>;
		delete: (id: string) => Promise<void>;
	};
	readonly useStore: ReturnType<typeof createEntityStore<TEntity>>;
}

interface CreateCrudStoreOptions {
	readonly apiClient: ApiClient;
	readonly entityPath: string;
}

export const createCrudStoreBundle = <
	TEntity extends CrudEntity,
	TAddInput,
	TUpdateInput extends { readonly id: string },
>(
	options: CreateCrudStoreOptions
): CrudStoreBundle<TEntity, TAddInput, TUpdateInput> => {
	const store = createEntityStore<TEntity>();

	const fetchAll = async (): Promise<TEntity[]> => {
		const state = store.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const entities = await options.apiClient.get<TEntity[]>(`/get/${options.entityPath}`);
			store.getState().setMany(entities ?? []);
			return entities;
		});
	};

	const fetchById = async (id: string): Promise<TEntity> => {
		const state = store.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const entities = await options.apiClient.get<TEntity[]>(`/get/${options.entityPath}`, {
				$filter: `id eq ${id}`,
			});
			const entity = entities[0];
			if (!entity) {
				throw new Error(`${options.entityPath}.not-exist`);
			}
			store.getState().upsertOne(entity);
			return entity;
		});
	};

	const add = async (input: TAddInput): Promise<TEntity> => {
		const state = store.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const entity = await options.apiClient.post<TEntity>(`/add/${options.entityPath}`, input);
			store.getState().upsertOne(entity);
			return entity;
		});
	};

	const update = async (input: TUpdateInput): Promise<TEntity> => {
		const state = store.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const entity = await options.apiClient.post<TEntity>(`/update/${options.entityPath}/${input.id}`, input);
			store.getState().upsertOne(entity);
			return entity;
		});
	};

	const remove = async (id: string): Promise<void> => {
		const state = store.getState();
		await runStoreAction(state.setLoading, state.setError, async () => {
			await options.apiClient.delete<unknown>(`/delete/${options.entityPath}/${id}`);
			store.getState().removeOne(id);
		});
	};

	return {
		useStore: store,
		actions: {
			fetchAll,
			fetchById,
			add,
			update,
			delete: remove,
		},
	};
};
